import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ShieldAlert, RefreshCw } from "lucide-react";
import { api, LOGO_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const ACTION_COLOR = {
  login_success: "#166534",
  login_failed: "#D9381E",
  login_locked: "#D9381E",
  mfa_failed: "#D9381E",
  mfa_verified: "#166534",
  mfa_enabled: "#166534",
  mfa_disabled: "#B45309",
  credentials_changed: "#B45309",
  credentials_change_failed: "#D9381E",
  content_updated: "#1E3A8A",
  content_reset: "#B45309",
  content_rolled_back: "#7C2D12",
  content_scheduled_applied: "#166534",
  product_updated: "#1E3A8A",
  product_scheduled_applied: "#166534",
  image_uploaded: "#166534",
  image_deleted: "#B45309",
  schedule_created: "#1E3A8A",
  schedule_deleted: "#B45309",
  logout: "#6B7280",
};

export default function AdminAudit() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit");
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) nav("/admin/login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user === false) { nav("/admin/login"); return; }
    if (user) load();
  }, [user]);

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header className="border-b border-black/10 bg-white sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-sm text-[#6B7280] hover:text-[#111] inline-flex items-center gap-1"><ArrowLeft size={14}/> Console</Link>
            <img src={LOGO_URL} alt="B-Raw" className="h-8 w-auto ml-3" />
          </div>
          <Button variant="outline" onClick={load} className="rounded-full border-black/10" data-testid="audit-refresh"><RefreshCw size={14} className="mr-2"/> Refresh</Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-2">
          <ShieldAlert className="text-[#D9381E]" />
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-[#111]">Audit Log</h1>
        </div>
        <p className="text-sm text-[#6B7280] mt-1">Every login attempt, credential change, and content edit is recorded here with IP + timestamp.</p>

        <div className="mt-6 bg-white border border-black/10 rounded-2xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>IP</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="audit-table-body">
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-[#6B7280]">Loading…</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-[#6B7280]">No activity yet.</TableCell></TableRow>
              ) : items.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs text-[#6B7280] whitespace-nowrap">{new Date(r.at).toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge style={{ backgroundColor: ACTION_COLOR[r.action] || "#111" }} className="text-white hover:opacity-90">{r.action}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">{r.actor}</TableCell>
                  <TableCell className="text-xs text-[#6B7280]">{r.ip}</TableCell>
                  <TableCell className="text-xs text-[#4B5563] max-w-[380px] truncate" title={JSON.stringify(r.details)}>{Object.keys(r.details || {}).length ? JSON.stringify(r.details) : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
}
